import {inject, Injectable, signal} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {firstValueFrom} from 'rxjs';
import {toObservable} from '@angular/core/rxjs-interop';
import {environment} from '@env/environment';
import {LoginService} from './login.service';

export type SaleMode = 'TRADICIONAL' | 'POS';
export interface UserPreferences {saleMode: SaleMode | null;}

@Injectable({providedIn: 'root'})
export class UserPreferencesService {
    private readonly http = inject(HttpClient);
    private readonly login = inject(LoginService);
    private readonly url = `${environment.endpointApi}/me/preferences`;
    private owner: number | null = null;
    private revision = 0;
    private writeRevision = 0;
    readonly preferences = signal<UserPreferences>({saleMode: null});
    readonly loaded = signal(false);
    readonly changes$ = toObservable(this.preferences);
    private pendingLoad?: Promise<UserPreferences>;

    get saleMode(): SaleMode | null {
        return this.owner === (Number(this.login.getUser()?.id) || null) ? this.preferences().saleMode : null;
    }

    load(): Promise<UserPreferences> {
        const owner = Number(this.login.getUser()?.id) || null;
        if (this.owner !== owner) {
            this.owner = owner;
            this.pendingLoad = undefined;
            this.preferences.set({saleMode: null});
            this.loaded.set(false);
        }
        if (!owner) { return Promise.resolve({saleMode: null}); }
        if (this.pendingLoad) { return this.pendingLoad; }
        const revision = ++this.revision;
        const pending = this.read(owner, revision).finally(() => {
            if (this.pendingLoad === pending) { this.pendingLoad = undefined; }
        });
        this.pendingLoad = pending;
        return pending;
    }

    private async read(owner: number, revision: number): Promise<UserPreferences> {
        const data = await firstValueFrom(this.http.get<UserPreferences>(this.url));
        if (data?.saleMode !== null && data?.saleMode !== 'TRADICIONAL' && data?.saleMode !== 'POS') {
            throw new Error('No se pudo interpretar la preferencia de venta.');
        }
        if (owner !== (Number(this.login.getUser()?.id) || null)) { throw new Error('La sesión cambió.'); }
        if (revision === this.revision) {
            this.preferences.set(data);
            this.loaded.set(true);
        }
        return revision === this.revision ? data : this.preferences();
    }

    async save(saleMode: SaleMode, initial: boolean): Promise<void> {
        const owner = Number(this.login.getUser()?.id);
        const writeRevision = ++this.writeRevision;
        this.revision++;
        const data = await firstValueFrom(this.http.put<UserPreferences>(
            `${this.url}/sale-mode${initial ? '/initial' : ''}`, {saleMode}));
        if (owner !== Number(this.login.getUser()?.id)) { throw new Error('La sesión cambió. Vuelve a intentarlo.'); }
        if (writeRevision === this.writeRevision) {
            this.revision++; // A confirmed write also supersedes reads that started while it was saving.
            this.owner = owner;
            this.preferences.set(data);
            this.loaded.set(true);
        }
    }
}
